/**
 * Echo Hunt -- live (DB-backed) wiring for the weaver (P8).
 *
 * Per the ground rules (§16.0), the weaving logic lives in the pure `Weave.ts`; this thin `*_Live.ts`
 * wrapper is where the profile's world pool / schematic rules / model settings are read and where the
 * round's context is assembled.
 *
 * Three responsibilities:
 *  1. **World-pool seeding** (§8.5). A profile created before P8 has `worldPool: []`, and a story
 *     cannot start without a world. `EnsureWorldPool_Live` seeds the six canonical worlds once, and is
 *     idempotent: a pool that already contains worlds is left completely alone, so a user who deleted
 *     a world they disliked does not get it back on every open.
 *  2. **Context assembly** (`GetWeaveContext_Live`). Picks the world for the round -- from an existing
 *     story when there is one, otherwise a random world from the pool (§8.3) -- and asks
 *     `Characters_Live` for the character, so P9's round builder only has to call one function. The
 *     character pool itself lives there, next to the story -> character resolution it has to agree with.
 *  3. **The dev-tool path** (`WeavePool_Live`), which weaves a pool the Settings tab just built.
 *
 * The world pool is IMPORTANT to keep as `echoProfiles.worldPool` (a JSON column): there is no
 * `echoWorlds` table, and adding one for six editable documents would buy nothing (§16.2 P2 layout).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.1, §8.2, §8.3, §8.5, §16.2 P8.
 */
import {GetAsync} from "mobx-graphlink";
import {GetMyEchoProfile} from "./Profiles_Live.js";
import {GetCheckOptionsFromProfile} from "./Bullets_Live.js";
import {EchoStory, GetEchoStories} from "../Server/DB/echoStories/@EchoStory.js";
import {GetEchoRoundsForStory} from "../Server/DB/echoRounds/@EchoRound.js";
import {UpdateEchoProfile} from "../Server/Commands/EchoProfileCommands.js";
import {MeID} from "../Server/DB/users.js";
import {liveLLMBackend} from "./LLM_Live.js";
import {LLMCallCounter} from "./LLM.js";
import {EchoWorld, RoundSlot} from "./Types.js";
import {
	ApplyWeave,
	WeaveBullet,
	WeaveContext,
	WeaveOptions,
	WeaveResult,
	WeaveStory,
} from "./Weave.js";
import {
	DEFAULT_SCHEMATIC_RULES,
	FRAME_DREAMING_BUT_AWAKE,
	GetSeedWorlds,
	PickRandomWorld,
} from "./Worlds.js";
import {ResolveNextCharacter_Live} from "./Characters_Live.js";
import {
	CurrentWorldId,
	GetDoorState,
	IsActiveStorySelectionStale,
	PendingWorldTransition,
	PickDoorTarget,
	SelectActiveStory,
} from "./Story.js";

// world pool (§8.5)
// ==========

/** The profile's worlds. Never null: a profile with no pool yields an empty array, so a caller can
 * check `.length` instead of null-guarding twice. */
export function GetWorldPool_Live(): EchoWorld[] {
	return GetMyEchoProfile()?.worldPool ?? [];
}

/** True when at least one world exists, i.e. when a story can be started. Deliberately NOT a readiness
 * condition for the dev tool: the dev tool seeds the pool itself (that is how you get the first one). */
export function IsWorldPoolReady(): boolean {
	return GetWorldPool_Live().length > 0;
}

let seedInFlight: Promise<void>|n = null;

/** Seeds the six canonical worlds into a profile that has none (§8.5), and the default schematic rules
 * if they were never set (§8.1). Idempotent, and safe to call on every mount: it writes only when the
 * corresponding list is empty, so an edit the user made is never overwritten.
 *
 * The `seedInFlight` guard mirrors `EnsureEchoProfile_Live`: two panels mounting at once would
 * otherwise both see an empty pool and both write, producing twelve worlds. */
export async function EnsureWorldPool_Live(): Promise<void> {
	// wrapped in `GetAsync` because this runs from a `useEffect`: the caller's `.catch()` would otherwise
	// swallow the cold-read bail and the pool would silently never be seeded
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return;

	const hasWorlds = (profile.worldPool ?? []).length > 0;
	const hasRules = (profile.schematicRules ?? []).length > 0;
	if (hasWorlds && hasRules) return;

	seedInFlight ??= (async()=>{
		const updates: {worldPool?: EchoWorld[]|n, schematicRules?: string[]|n} = {};
		if (!hasWorlds) updates.worldPool = GetSeedWorlds();
		if (!hasRules) updates.schematicRules = [...DEFAULT_SCHEMATIC_RULES];
		await new UpdateEchoProfile({id: profile.id, updates}).RunOnServer();
	})().finally(()=>{
		seedInFlight = null;
	});
	await seedInFlight;
}

/** Adds one world to the pool, or replaces the one with the same id (the Settings tab's bible editor).
 * Returns false when there is no profile to write to. */
export async function UpsertWorld_Live(world: EchoWorld): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;

	const pool = [...(profile.worldPool ?? [])];
	const index = pool.findIndex(a=>a.id == world.id);
	if (index >= 0) pool[index] = world;
	else pool.push(world);

	await new UpdateEchoProfile({id: profile.id, updates: {worldPool: pool}}).RunOnServer();
	return true;
}

/** Removes one world from the pool. Refuses to remove the LAST world: a story needs somewhere to be, and
 * an empty pool is a silent trap (rounds that cannot be built) rather than a state worth supporting. */
export async function RemoveWorld_Live(worldId: string): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;

	const pool = (profile.worldPool ?? []).filter(a=>a.id != worldId);
	if (pool.length == 0) return false;

	await new UpdateEchoProfile({id: profile.id, updates: {worldPool: pool}}).RunOnServer();
	return true;
}

/** Writes a whole world pool at once (the "restore the seeded worlds" action). */
export async function SetWorldPool_Live(worlds: EchoWorld[]): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {worldPool: worlds}}).RunOnServer();
	return true;
}

/** Writes the user's schematic rules (§8.1). An empty list is a legitimate choice -- it just means the
 * weaver is told no extra constraints -- so this does not re-seed when cleared. */
export async function SetSchematicRules_Live(rules: string[]): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {schematicRules: rules}}).RunOnServer();
	return true;
}

// stories (§8.3)
// ==========

/** The user's SELECTED running (not ended) story, if any.
 *
 * P11 follow-up: the user may have any number of stories running at once and pick which one is active, so
 * this resolves `echoProfiles.activeStoryId` first and falls back to the newest non-ended story. Two
 * properties make that fallback the right default rather than a compromise:
 *
 *  - a profile created before the field existed has no id, and must behave exactly as it did;
 *  - a story that ended, or was deleted, leaves a dangling id behind. Falling back means the panel shows a
 *    playable story rather than nothing, and the UI REPORTS the repair rather than hiding it.
 *
 * Selection lives on the PROFILE rather than as an `active` flag on each story: a per-row flag needs an
 * exactly-one invariant enforced on every write (and a bulk update on every switch), for information that is
 * really "what is this user looking at".
 *
 * SYNC. `echoStories` is the one collection in this feature that no tab renders, so it is never
 * subscribed to until something reads it -- and reading a cold accessor throws a `BailError`, which only
 * a React `observer` catches. From a click handler, wrap the CALL SITE: `await GetAsync(()=>
 * GetWeaveContext_Live({...}))`. That single wrap covers every accessor touched inside, which is why
 * there are no async variants of these helpers. */
export function GetActiveStory_Live(): EchoStory|n {
	const userID = MeID();
	if (userID == null) return null;
	return SelectActiveStory(GetEchoStories(userID), GetMyEchoProfile()?.activeStoryId);
}

/** Every story that is still running, newest first -- the Story tab's switcher (P11 follow-up). Most recent
 * first, so the fallback selection (`SelectActiveStory` in `Story.ts`) is the first entry and the list reads
 * in the same order the drop-down shows. */
export function GetRunningStories_Live(): EchoStory[] {
	const userID = MeID();
	if (userID == null) return [];
	return [...GetEchoStories(userID)]
		.filter(a=>a.ended != true)
		.sort((a, b)=>b.createdAt - a.createdAt);
}

/** True when the profile's stored selection points at a story that has ended or been deleted, so the UI can
 * say the selection was repaired rather than silently showing a different story. */
export function IsActiveStorySelectionStale_Live(): boolean {
	const userID = MeID();
	if (userID == null) return false;
	return IsActiveStorySelectionStale(GetEchoStories(userID), GetMyEchoProfile()?.activeStoryId);
}

/** The most recently ENDED story, so a new story can avoid re-using its character and world (§8.3).
 * Null when there is none, which simply means there is nothing to exclude. */
export function GetPreviousEndedStory_Live(excludeStoryId?: string|n): EchoStory|n {
	const userID = MeID();
	if (userID == null) return null;
	return GetEchoStories(userID)
		.filter(a=>a.ended == true && a.id != excludeStoryId)
		.sort((a, b)=>b.createdAt - a.createdAt)[0] ?? null;
}

/** The context one weave needs (§8.1), assembled from the profile and the active story.
 *
 * When there is an active story, its world and character are used and its `summary` is carried in --
 * that continuity is the whole point of §8.3. The character is resolved from the POOL by id (§8.7), so
 * editing it mid-story is reflected here on the next round. When there is no story, a fresh world is
 * drawn from the pool (excluding the previous story's world, so two characters in a row do not start in
 * the same place) and a character is drawn from the pool (excluding the previous story's).
 *
 * §11.5: three story-level facts are derived here rather than read off the story row, because all three
 * can be worked out from the rounds and the visit history:
 *  - WHERE the story is (`CurrentWorldId`, W1);
 *  - what the next installment must NARRATE as an arrival (`PendingWorldTransition`, W4);
 *  - whether a door is already in play -- a staged one for the character to decide on, or a new one the
 *    weaver may propose to the player (`GetStagedDoor_Live` / `doorTarget`, W2 + W3).
 *
 * Returns null when there is nothing to be in or nobody to be:
 *  - no world in the pool AND no active story;
 *  - no character in the pool AND no active story;
 *  - an active story whose character was DELETED (null on purpose: continuing would silently attach the
 *    story's summary, suspicion and world to a different person).
 *
 * The caller distinguishes those by checking the pools, so a user gets "add a world" / "add a
 * character" / "this story's character is gone" rather than one vague failure.
 *
 * SYNC -- from an async path, wrap the call: `await GetAsync(()=>GetWeaveContext_Live({...}))`. */
export function GetWeaveContext_Live(options: {rng: () => number, excludeWorldId?: string|n}): WeaveContext|null {
	const profile = GetMyEchoProfile();
	if (profile == null) return null;

	const activeStory = GetActiveStory_Live();
	const previousStory = GetPreviousEndedStory_Live(activeStory?.id);
	const character = ResolveNextCharacter_Live(activeStory, previousStory, options.rng);
	if (character == null) return null;

	if (activeStory != null) {
		// rounds read straight from the accessor: `Story_Live` imports THIS module, and the round list is the
		// only thing needed here, so going through it would add a cycle for no gain
		const storyRounds = GetEchoRoundsForStory(activeStory.id);
		const currentWorldId = CurrentWorldId(activeStory, storyRounds);
		const world = (profile.worldPool ?? []).find(a=>a.id == currentWorldId)
			// a world deleted from the pool is RECOVERED before this point by `EnsureStoryWorld_Live`, which
			// also records the move; this remains as a last-resort guard so a round is never failed over it
			?? PickRandomWorld(profile.worldPool, options.rng, options.excludeWorldId);
		if (world == null) return null;

		const context: WeaveContext = {
			world,
			character,
			frame: FRAME_DREAMING_BUT_AWAKE,
			summary: activeStory.summary,
			schematicRules: profile.schematicRules ?? [],
		};

		// §11.5 W4: the arrival, when the story moved and no round has been written since
		const transition = PendingWorldTransition(activeStory, storyRounds.length);
		if (transition != null) {
			context.worldChange = {
				fromWorldName: transition.fromWorldName,
				cause: transition.cause,
				doorDescription: transition.doorDescription,
			};
		}

		// §11.5 W2: a door the player accepted last round is now the CHARACTER's question
		const doorState = GetDoorState(storyRounds);
		if (doorState?.awaitingCharacter == true) {
			context.stagedDoor = {
				description: doorState.offer.description,
				targetWorldId: doorState.offer.targetWorldId ?? null,
			};
		} else if (doorState == null) {
			// §11.5 W3: no door in play, so the weaver may propose one -- and gets the target UP FRONT so the
			// far side can be described honestly. An override skips the proposal: the player pressing it has
			// already made the authorial decision, so the door is staged for the character directly.
			const target = PickDoorTarget({currentWorldId: world.id, pool: profile.worldPool, rng: options.rng});
			if (target != null) {
				context.doorTarget = {id: target.id, name: target.name, bible: target.bible};
				// read straight off the row: `IsDoorOverrideRequested` lives in `Story_Live`, which imports this module
				if (activeStory.doorOverrideRequested == true) context.requestDoor = true;
			}
		}

		return context;
	}

	const world = PickRandomWorld(profile.worldPool, options.rng, options.excludeWorldId);
	if (world == null) return null;

	return {
		world,
		character,
		frame: FRAME_DREAMING_BUT_AWAKE,
		summary: null,
		schematicRules: profile.schematicRules ?? [],
	};
}

// the weave
// ==========

/** Weaves a pool of slots with the live profile settings and backend (§16.2 P8).
 *
 * Takes the slots and adapts them to the label-free `WeaveBullet` shape itself, so a caller can hand
 * over a pool straight from P7 without first stripping the labels -- and so the stripping cannot be
 * forgotten at a call site (§16.0 label hygiene).
 *
 * Returns a result whose `output` is null when the weave failed; the caller must not play the round.
 */
export async function WeaveSlots_Live(slots: RoundSlot[], options: {context: WeaveContext, counter?: LLMCallCounter|n, maxAttempts?: number|n}): Promise<WeaveResult> {
	// wrapped: this is called from click handlers, where nothing catches a cold-read bail
	const profile = await GetAsync(()=>GetMyEchoProfile());

	const weaveOptions: WeaveOptions = {
		context: options.context,
		// only the id and the text cross this boundary: no `sourceKind`, no `sourceId` (§16.0)
		bullets: ToWeaveBullets(slots),
		maxAttempts: options.maxAttempts ?? undefined,
		checkOptions: GetCheckOptionsFromProfile(),
		counter: options.counter,
		// the per-role `weaver` override is resolved by the backend from the profile; passing null here
		// keeps the resolution in one place (`LiveLLMBackend` + `ResolveModelForRole`)
		model: profile?.modelRoles?.weaver ?? null,
		// Settings -> Weaver (off by default): let the weaver reword freely while bridging (2026-10-21)
		includeParaphraseNote: profile?.weaveParaphraseNote == true,
	};
	return WeaveStory(liveLLMBackend, weaveOptions);
}

/** Convenience for the dev tool: weave a pool in one step, returning the woven slots as well as the raw
 * result. The slots come back in STORY order with `wovenText`/`finalText` filled, or null when the
 * weave failed (in which case `result` still carries the problems for display). */
export async function WeavePool_Live(slots: RoundSlot[], options: {context: WeaveContext, counter?: LLMCallCounter|n}): Promise<{result: WeaveResult, slots: RoundSlot[]|null}> {
	const result = await WeaveSlots_Live(slots, options);
	if (result.output == null) return {result, slots: null};
	return {result, slots: ApplyWeave(slots, result.output)};
}

/** Strips the labels off a pool (§16.0). Exported because it is the one place the label-stripping rule
 * is expressed, and the dev tool uses it to check that nothing leaks. */
export function ToWeaveBullets(slots: RoundSlot[]|n): WeaveBullet[] {
	return (slots ?? []).map(slot=>({slotId: slot.slotId, text: slot.finalText.length > 0 ? slot.finalText : slot.reshapedText}));
}
